/**
 * Your account: change your password, or sign out.
 *
 * Small on purpose. There is exactly one thing here somebody needs and cannot
 * do anywhere else, and burying it under a settings tree would be pretending
 * there is more.
 *
 * A guest reaches this page too — from the landing screen's account area — and
 * is told plainly that there is nothing to change, rather than being shown a
 * form that will refuse them.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { changePassword } from '../net/session.js';
import { hasAccount, useSession } from '../session-context.js';
import { signOut } from '../net/auth.js';
import { describeError } from './errors.js';

export function Account() {
  const state = useSession();
  const { session } = state;

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const signedIn = hasAccount(state);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!session || busy) return;

    setBusy(true);
    setError(null);
    setDone(false);
    try {
      const result = await changePassword(session, current, next);
      if (!result.ok) {
        setError(result.message ?? 'That could not be changed.');
        return;
      }
      setDone(true);
      // Nothing useful survives a success, and leaving a password sitting in a
      // field is the sort of thing somebody's shoulder reads.
      setCurrent('');
      setNext('');
    } catch (caught) {
      setError(describeError(caught, 'Could not reach the server.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page narrow">
      <h1 className="page-title">Your account</h1>

      {signedIn ? (
        <form onSubmit={submit}>
          <h2 className="section-title">Change your password</h2>

          <label className="field">
            <span className="field-label">Current password</span>
            <input
              className="text-input"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(event) => setCurrent(event.target.value)}
            />
          </label>

          <label className="field">
            <span className="field-label">New password</span>
            <input
              className="text-input"
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(event) => setNext(event.target.value)}
            />
          </label>

          {error ? <p className="banner error">{error}</p> : null}
          {done ? <p className="banner win">Password changed.</p> : null}

          <button
            type="submit"
            className="big-button primary"
            disabled={busy || current.length === 0 || next.length === 0}
          >
            {busy ? 'Changing…' : 'Change password'}
          </button>
        </form>
      ) : (
        <p className="field-help">
          You are playing as a guest on this device, so there is no password to
          change. Create an account to own games and keep them.
        </p>
      )}

      <div className="page-links">
        {signedIn ? (
          <button
            type="button"
            className="text-button"
            onClick={() => {
              void (async () => {
                await state.adopt(await signOut());
              })();
            }}
          >
            Log out
          </button>
        ) : (
          <Link className="text-button" to="/register">
            Create an account
          </Link>
        )}
        <Link className="text-button" to="/">
          Back
        </Link>
      </div>
    </div>
  );
}

